import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { updateGTMUserHash } from "../../../utils/analytics";
import { IReduxState } from "../reducers.interfaces";

/**
 * Save the guest email address for checkout.
 *
 * This thunk saves the guest email address to the basket/order and updates
 * the Google Tag Manager user hash for analytics tracking. Used during
 * checkout when guest users provide their email address.
 *
 * @returns {Promise<{owner_email: string}>} Promise resolving to the saved email address
 *
 * @example
 * // Save guest email during checkout
 * dispatch(saveEmailAddress());
 *
 * @sideEffects
 * - Reads guest email from `state.form.guest_email`
 * - Calls updateGTMUserHash to update analytics tracking
 * - Makes API call to save email via checkoutAPI.saveEmailAddress
 * - Backend associates email with basket for guest checkout
 */
export const saveEmailAddress = createAsyncThunk(
    "checkout/saveEmailAddress",
    async (_, { getState }): Promise<{ owner_email: string }> => {
        const state = getState() as { checkout: IReduxState };
        const guestEmail = state.checkout.form.guest_email;
        updateGTMUserHash(guestEmail);
        return checkoutAPI.saveEmailAddress(guestEmail);
    },
);
